import React, { Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import PrivateRoute from "./components/PrivateRoute";
import { UserProvider } from "context/User";
import { createTheme, ThemeProvider } from "@mui/material";
import { ErrorBoundary } from "components/ErrorBoundary";
import { CompanyProvider } from "context/Company";
import ReactGA from "react-ga4";
import { CartProvider } from "context/Cart";
import { PricingProvider } from "context/Pricing";
import { ProductProvider } from "context/Product";
import { AddressProvider } from "context/Address";
import { ProductDevelopmentProvider } from "context/ProductDevelopment";
import { NotificationProvider } from "context/Notification";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { CircularProgress, Box } from "@mui/material";

// Lazy-loaded page components
const FixtureProfiles = React.lazy(() => import("components/MiscRoutes/FixtureProfiles"));
const Importer = React.lazy(() => import("components/MiscRoutes/Importer"));
const ProfileEditor = React.lazy(() => import("components/ProfileEditor/ProfileEditor"));
const Overview = React.lazy(() => import("components/Home/Overview"));
const CreatePO = React.lazy(() => import("components/Products/CreatePO"));
const OrderHistory = React.lazy(() => import("components/OrderHistory/OrderHistory"));
const Checkout = React.lazy(() => import("components/Checkout/Checkout"));
const MediaLibrary = React.lazy(() => import("components/MediaLibrary/MediaLibrary"));
const Admin = React.lazy(() => import("components/Admin/Admin"));
const Help = React.lazy(() => import("components/Help/Help"));
const FAQPage = React.lazy(() => import("components/Help/FAQ"));
const QuoteBuilder = React.lazy(() => import("components/Quote Builder/QuoteBuilder"));
const Status = React.lazy(() => import("components/Admin/Status"));
const SO = React.lazy(() => import("components/OrderHistory/SO"));
const MyFiles = React.lazy(() => import("components/MiscRoutes/MyFiles"));
const ThankYou = React.lazy(() => import("components/Checkout/ThankYou"));
const JotformPage = React.lazy(() => import("components/MiscRoutes/JotformPage"));
const EditUser = React.lazy(() => import("components/Admin/EditUser"));
const MyCompany = React.lazy(() => import("components/MyCompany/MyCompany"));
const Newsfeed = React.lazy(() => import("components/Newsfeed/Newsfeed"));
const ViewResellerInfoForm = React.lazy(() => import("components/Admin/Approval/ViewResellerInfoForm"));
const PendingOrders = React.lazy(() => import("components/OrderHistory/PendingOrders"));
const PO = React.lazy(() => import("components/OrderHistory/PO"));
const Account = React.lazy(() => import("components/Account/Account"));
const SingleProductPage = React.lazy(() => import("components/Products/SingleProductPage"));
const SingleProductInstance = React.lazy(() => import("components/Products/SingleProductInstance"));
const ResellerApplication = React.lazy(() => import("components/Account/Wizard/ResllerApplication"));
const Wizard = React.lazy(() => import("components/Account/Wizard/Wizard"));
const WebOrderCreateRefund = React.lazy(() => import("components/Admin/Web/Order/WebOrderCreateRefund"));
const WebCustomerDetails = React.lazy(() => import("components/Admin/Web/Customer/WebCustomerDetails"));
const DemoFeedbackForm = React.lazy(() => import("components/Interactions/ResellerInteractionForm"));
const SingleBaseProduct = React.lazy(() => import("components/Admin/BaseProducts/SingleBaseProduct"));
const ProductDevelopmentDashboard = React.lazy(() => import("components/ProductDevelopment/Dashboard"));
const NewPDProjectForm = React.lazy(() => import("components/ProductDevelopment/NewProject"));
const SinglePDProject = React.lazy(() => import("components/ProductDevelopment/SingleProject"));
const AllProductSales = React.lazy(() => import("components/Reports/AllProductSales"));
const SalesByReseller = React.lazy(() => import("components/Reports/SalesByReseller"));
const VendorList = React.lazy(() => import("components/ProductDevelopment/VendorList"));
const InteractionsList = React.lazy(() => import("components/Interactions/InteractionsList"));
const MattZone = React.lazy(() => import("components/Interactions/MattZone"));
const WebOrderDetails = React.lazy(() => import("components/Admin/Web/Order/WebOrderDetails"));
const MonthlyRepReportOverview = React.lazy(() => import("components/Interactions/MonthlyRepReportOverview"));
const Marketing = React.lazy(() => import("components/Marketing/MarketingContactList"));
const CustomerRequestsLayout = React.lazy(() => import("components/Admin/CustomerRequests/CustomerRequestsLayout"));
const CustomerRequestDetails = React.lazy(() => import("components/Admin/CustomerRequests/CustomerRequestDetails"));
const CreateOrderFromRequest = React.lazy(() => import("components/Admin/CustomerRequests/CreateOrderFromRequest"));
const PricingRulesAdmin = React.lazy(() => import("components/Admin/PricingRules/PricingRulesAdmin"));
const ManufacturingPage = React.lazy(() => import("components/Admin/Manufacturing/ManufacturingPage"));
const ReorderReport = React.lazy(() => import("components/Admin/ReorderReport/ReorderReport"));
const PartActivityReport = React.lazy(() => import("components/Admin/PartActivity/PartActivityReport"));
const RmaTurnaround = React.lazy(() => import("components/Admin/RmaTurnaround/RmaTurnaround"));
const QuarterlyFeaturesManager = React.lazy(() => import("components/Admin/QuarterlyFeatures/QuarterlyFeaturesManager"));
const ProductSheeterPage = React.lazy(() => import("tools/product-sheeter"));
const AtomZone = React.lazy(() => import("components/Interactions/AtomZone"));
const SalesDashboard = React.lazy(() => import("components/Admin/SalesDashboard/SalesDashboard"));
const BuildPriceList = React.lazy(() => import("components/Admin/PriceList/BuildPriceList"));

const LoadingFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
    </Box>
);

const App = () => {
    const theme = createTheme({
        typography: {
            fontFamily: "Poppins",
        },
        palette: {
            primary: { main: "#282bf6" },
            secondary: { main: "#081016" },
            warning: { main: "#f44336" },
        },
    });

    React.useEffect(() => {
        ReactGA.initialize("G-DQW0MK47SZ");
    }, []);

    return (
        <ErrorBoundary>
            <ThemeProvider theme={theme}>
                <NotificationProvider>
                <UserProvider>
                    <CartProvider>
                    <PricingProvider>
                        <ProductProvider>
                            <CompanyProvider>
                                <AddressProvider>
                                    <ProductDevelopmentProvider>

                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <Router>
                                                <Suspense fallback={<LoadingFallback />}>
                                                <Routes>
                                                    <Route
                                                        path="/account"
                                                        element={(
                                                            <PrivateRoute children={<Account />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/ach"
                                                        element={(
                                                            <JotformPage jotformId="220824430507145" />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/rep/marketing"
                                                        element={(
                                                            <JotformPage jotformId="230244344180143" />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/cc-auth"
                                                        element={(
                                                            <JotformPage jotformId="220935849652163" />
                                                        )}
                                                    />

                                                    {/* <Route
                                                        path="/demos"
                                                        element={(
                                                            <PrivateRoute children={<Demos />} admin={false} />
                                                        )}
                                                    /> */}
                                                    <Route
                                                        path="/interactions"
                                                        element={(
                                                            <PrivateRoute children={<InteractionsList />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/interaction-form"
                                                        element={(
                                                            <PrivateRoute children={<DemoFeedbackForm />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/interaction-form/:id"
                                                        element={(
                                                            <PrivateRoute children={<DemoFeedbackForm />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/rep-overview"
                                                        element={(
                                                            <PrivateRoute children={<MattZone />} admin={true} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/interaction-overview"
                                                        element={(
                                                            <PrivateRoute children={<MattZone />} admin={true} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/monthly-report-overview"
                                                        element={(
                                                            <PrivateRoute admin={true} >
                                                                <MonthlyRepReportOverview />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/files"
                                                        element={(
                                                            <PrivateRoute children={<MyFiles />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/fixtureprofiles"
                                                        element={(
                                                            <PrivateRoute children={<FixtureProfiles />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/profile-editor"
                                                        element={(
                                                            <PrivateRoute children={<ProfileEditor />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/products"
                                                        element={(
                                                            <PrivateRoute children={<CreatePO />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/products/instance/:serialNum"
                                                        element={(
                                                            <PrivateRoute children={<SingleProductInstance />} admin={true} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/products/:id"
                                                        element={(
                                                            <PrivateRoute children={<SingleProductPage />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/order-history"
                                                        element={(
                                                            <PrivateRoute children={<OrderHistory />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/order-history/:id"
                                                        element={(
                                                            <PrivateRoute children={<SO />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/pending-orders"
                                                        element={(
                                                            <PrivateRoute children={<PendingOrders />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/pending-orders/:id"
                                                        element={(
                                                            <PrivateRoute children={<PO />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/web-orders/:id"
                                                        element={(
                                                            <PrivateRoute children={<WebOrderDetails />} admin={true} />
                                                        )}
                                                    />

                                                    <Route
                                                        path="/web-customer/:id"
                                                        element={(
                                                            <PrivateRoute children={<WebCustomerDetails />} admin={true} />
                                                        )}
                                                    />

                                                    <Route
                                                        path="/create-refund/:id"
                                                        element={(
                                                            <PrivateRoute children={<WebOrderCreateRefund />} admin={true} />
                                                        )}
                                                    />

                                                    <Route
                                                        path="/status"
                                                        element={(
                                                            <PrivateRoute children={<Status />} admin={true} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/newsfeed"
                                                        element={(
                                                            <PrivateRoute children={<Newsfeed />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/company"
                                                        element={(
                                                            <PrivateRoute children={<MyCompany />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/company/:companyId"
                                                        element={(
                                                            <PrivateRoute children={<MyCompany />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/checkout"
                                                        element={(
                                                            <PrivateRoute children={<Checkout />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/checkout/thank-you"
                                                        element={(
                                                            <PrivateRoute children={<ThankYou />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/media-library"
                                                        element={(
                                                            <PrivateRoute children={<MediaLibrary />} admin={false} />
                                                        )}
                                                    />

                                                    <Route
                                                        path="/pd"
                                                        element={(
                                                            <PrivateRoute children={<ProductDevelopmentDashboard />} admin={true} />
                                                        )}
                                                    />

                                                    <Route
                                                        path="/pd/new"
                                                        element={(
                                                            <PrivateRoute children={<NewPDProjectForm />} admin={true} />
                                                        )}
                                                    />

                                                    <Route
                                                        path="/pd/:id"
                                                        element={(
                                                            <PrivateRoute children={<SinglePDProject />} admin={true} />
                                                        )}
                                                    />

                                                    {/* <Route
                                                        path="/pd/:id"
                                                        element={(
                                                            <PDProvider>
                                                                <PrivateRoute>
                                                                    <ProductDevelopmentSingleProject />
                                                                </PrivateRoute>
                                                            </PDProvider>
                                                        )}
                                                    /> */}

                                                    {/* <Route
                                                        path="/pd-overview"
                                                        element={(
                                                            <PrivateRoute children={<PDCenter />} admin={true} />
                                                        )}
                                                    /> */}
                                                    <Route
                                                        path="/vendors"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <VendorList />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/iris-quote-builder"
                                                        element={(
                                                            <PrivateRoute children={<QuoteBuilder />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin"
                                                        element={(
                                                            <PrivateRoute children={
                                                                <Admin />
                                                            } admin={true} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/pricing-rules"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <PricingRulesAdmin />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/manufacturing"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <ManufacturingPage />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/reorder-report"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <ReorderReport />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    {/* Not linked in the Admin nav yet - direct URL only */}
                                                    <Route
                                                        path="/admin/price-list"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <BuildPriceList />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/part-activity"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <PartActivityReport />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/rma-turnaround"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <RmaTurnaround />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/quarterly-features"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <QuarterlyFeaturesManager />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/product-sheeter"
                                                        element={(
                                                            <PrivateRoute>
                                                                <ProductSheeterPage />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/admin/sales-dashboard"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <SalesDashboard />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/atom-zone"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <AtomZone />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/marketing"
                                                        element={(
                                                            <PrivateRoute children={
                                                                <Marketing />
                                                            } admin={true} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/reports"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <AllProductSales />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    {/* <Route
                                                        exact
                                                        path="/reports/sales-by-reseller"
                                                        element={(
                                                            <PrivateRoute>
                                                                <SalesByReseller />
                                                            </PrivateRoute>
                                                        )}
                                                    /> */}
                                                    <Route
                                                        path="/reports/sales-by-reseller"
                                                        element={(
                                                            <PrivateRoute admin={ false }>
                                                                <SalesByReseller />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/edit-user/:uid"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <EditUser />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/base-products/:id"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <SingleBaseProduct />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/base-products"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <SingleBaseProduct />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/view-reseller-info-form/:id"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <ViewResellerInfoForm />
                                                            </PrivateRoute>
                                                        )}
                                                    />
                                                    <Route
                                                        path="/edit-company/:companyId"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <MyCompany isAdmin />
                                                            </PrivateRoute>
                                                        )}
                                                    />

                                                    <Route
                                                        path="/customer-requests"
                                                        element={(
                                                            <PrivateRoute admin>
                                                                <CustomerRequestsLayout />
                                                            </PrivateRoute>
                                                        )}
                                                    >
                                                        <Route
                                                            path=":id"
                                                            element={<CustomerRequestDetails />}
                                                        />
                                                        <Route
                                                            path=":id/create-order"
                                                            element={<CreateOrderFromRequest />}
                                                        />
                                                    </Route>

                                                    <Route
                                                        path="/help/faq"
                                                        element={
                                                            <PrivateRoute children={<FAQPage />} admin={false} />
                                                        }
                                                    />
                                                    <Route
                                                        path="/help"
                                                        element={
                                                            <PrivateRoute children={<Help />} admin={false} />
                                                        }
                                                    />
                                                    <Route
                                                        path="/apply"
                                                        element={<ResellerApplication />}
                                                    />
                                                    <Route
                                                        path="/approval"
                                                        element={(
                                                            <PrivateRoute children={<Wizard hideNav={true} />} admin={false} />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/importer"
                                                        element={(
                                                            <PrivateRoute children={<Importer />} admin={true} />
                                                        )}
                                                    />
                                                    <Route path="/" element={(<PrivateRoute children={<Overview />} admin={false} />)} />
                                                </Routes>
                                                </Suspense>
                                            </Router>
                                        </LocalizationProvider>
                                    </ProductDevelopmentProvider>
                                </AddressProvider>
                            </CompanyProvider>
                        </ProductProvider>
                    </PricingProvider>
                    </CartProvider>
                </UserProvider>
                </NotificationProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default App;
